import { Button } from "@mui/joy";
import { Box, Tab, Tabs, Tooltip } from "@mui/material";
import {
  DataGridPremium as DataGridPro,
  GridColumns,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  Toolbar,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import moment from "moment";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  defaultDataGridProps,
  injectDefaultColumnProps,
} from "../../../constants";
import { Project } from "../../../entities/project";
import { useProjects } from "../../../redux/project/projectHooks";
import { useProjectTypes } from "../../../redux/projectType/projectTypeHooks";
import { useAppSelector } from "../../hooks";
import NoRowsOverlay from "../../noRowsOverlay/noRowsOverlay";
import {
  ProjectsTab,
  projectsTab,
  setFormOpen,
  setProjectsTab,
  setSelectedProjectID,
} from "../../../redux/filmsReducer";
import { useSelector } from "react-redux";
import {
  projectMilestoneLabels,
  projectProgressAccent,
  projectProgressClass,
  projectProgressRatio,
  projectProgressRowStyles,
  projectProgressStep,
} from "../../../helpers/projectProgress";

/**
 * Which tab a project belongs to. Archiving is a deliberate act, so it wins:
 * an archived project is only ever under Archived, however far along it is.
 *
 * @param {object} project Project to place.
 * @param {string} tab Tab to test against.
 * @returns {boolean} True if the project belongs in that tab.
 */
function inTab(project: Project, tab: ProjectsTab): boolean {
  if (project.archived) return tab === "archived";
  return tab === (project.completed() ? "completed" : "active");
}

/**
 * Project list component
 *
 * @returns {ReactElement} Users List
 */
export default function ProjectsList() {
  const tab = useSelector(projectsTab);
  const { projects: _projects } = useProjects();
  const projects = _projects?.filter((p) => inTab(p, tab));

  const { projectTypes } = useProjectTypes();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const apiRef = useGridApiRef();

  const handleClick = (id: number) => {
    navigate(`${id}`);
    dispatch(setSelectedProjectID(id));
  };

  const columns: GridColumns<Project> = [
    {
      field: "id",
      headerName: "ID",
      editable: false,
      type: "string",
      width: 40,
    },
    {
      field: "name",
      headerName: "Name",
      editable: false,
      type: "string",
      flex: 1,
    },
    {
      field: "status",
      headerName: "Status",
      editable: false,
      type: "string",
    },
    {
      field: "progress",
      headerName: "Progress",
      editable: false,
      filterable: false,
      width: 110,
      // Numeric so the comparator orders by size rather than lexically, but
      // left-aligned like the bars it sits behind.
      type: "number",
      align: "left",
      headerAlign: "left",
      // Sorting a rendered cell needs a real value behind it. The fraction, not
      // the raw count — see projectProgressRatio.
      valueGetter: (_value: unknown, row: Project) =>
        projectProgressRatio(
          row?.milestonesReached?.() ?? 0,
          row?.milestonesTotal?.() ?? 0
        ),
      renderCell: (params) => <ProgressStepper project={params.row} />,
    },
    {
      field: "invoiceNumber",
      headerName: "Invoice",
      editable: false,
      type: "string",
      valueFormatter: (valueOrParams: any) => {
        const value = valueOrParams?.value ?? valueOrParams;
        return `FLMS${value}`;
      },
    },
    {
      field: "referenceNumber",
      headerName: "Ref",
      editable: false,
      type: "string",
    },
    {
      field: "projectTypeID",
      headerName: "Type",
      editable: false,
      type: "string",
      width: 140,
      valueFormatter: (valueOrParams: any) => {
        const value = valueOrParams?.value ?? valueOrParams;
        return projectTypes?.find((pt) => pt.id === value)?.name;
      },
    },
    {
      field: "createdAt",
      width: 120,
      headerName: "Start Date",
      editable: false,
      type: "string",
      valueFormatter: (valueOrParams: any) => {
        const value = valueOrParams?.value ?? valueOrParams;
        return moment(value).format("ll");
      },
    },
  ];

  return (
    <DataGridPro
      {...defaultDataGridProps}
      // The row's green wash is the status signal now. The old per-cell
      // ".lock" tint would stack on top of it and blur the steps apart, and
      // nothing in this grid is editable for it to be guarding anyway.
      getCellClassName={(p) => (p.field === "id" ? "cellReadOnly" : "")}
      getRowClassName={(p) =>
        projectProgressClass(
          projectProgressStep(
            p.row.milestonesReached(),
            p.row.milestonesTotal()
          )
        )
      }
      apiRef={apiRef}
      rows={projects ?? []}
      columns={injectDefaultColumnProps(columns)}
      onRowClick={(p) => handleClick(p.row.id)}
      showToolbar
      slots={{
        toolbar: ProjectToolbar,
        noRowsOverlay: NoRowsOverlay,
      }}
      slotProps={{
        cell: { tabIndex: 1 },
      }}
      sx={{ flex: 1, ...projectProgressRowStyles() }}
      initialState={{ pinnedColumns: { right: ["action"] } }}
    />
  );
}

/**
 * One segment per milestone, filled up to where the project has got to. Reads
 * as a stepper at a glance and names each milestone on hover, which the row
 * colour alone can't do.
 *
 * @param {object} props Component props.
 * @param {object} props.project Project to chart.
 * @returns {ReactElement} The stepper.
 */
function ProgressStepper({ project }: { project: Project }) {
  const milestones = project.milestones();
  const labels = projectMilestoneLabels(project.union);
  const step = projectProgressStep(
    project.milestonesReached(),
    milestones.length
  );

  return (
    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", height: 1 }}>
      {milestones.map((reached, i) => (
        <Tooltip key={labels[i]} title={`${labels[i]}${reached ? " ✓" : ""}`}>
          <Box
            sx={{
              width: 14,
              height: 6,
              borderRadius: 3,
              backgroundColor: reached
                ? projectProgressAccent(step)
                : "rgba(0, 0, 0, 0.12)",
            }}
          />
        </Tooltip>
      ))}
    </Box>
  );
}

function ProjectToolbar() {
  const dispatch = useDispatch();
  const tab = useSelector(projectsTab);
  const { projects } = useProjects();
  const count = (t: ProjectsTab) =>
    projects?.filter((p) => inTab(p, t)).length ?? 0;

  return (
    <Toolbar>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          p: 1,
          gap: 2,
          alignItems: "center",
          width: "100%",
        }}
      >
        <Button
          variant="soft"
          size="sm"
          onClick={() =>
            dispatch(setFormOpen({ isOpen: true, formID: "project" }))
          }
        >
          + New Project
        </Button>

        <Tabs
          value={tab}
          onChange={(_, value) => dispatch(setProjectsTab(value))}
          sx={{ minHeight: 0, "& .MuiTab-root": { minHeight: 0, py: 1 } }}
        >
          <Tab value="active" label={`Active (${count("active")})`} />
          <Tab value="completed" label={`Completed (${count("completed")})`} />
          <Tab value="archived" label={`Archived (${count("archived")})`} />
        </Tabs>

        <QuickFilter
          expanded
          style={{ flex: 1, display: "flex", alignItems: "center" }}
        >
          <QuickFilterControl size="small" fullWidth placeholder="Search..." />

          <QuickFilterClear />
        </QuickFilter>
      </Box>
    </Toolbar>
  );
}

export type ProjectListProps = {
  onCreate?: () => void;
};
